// src/components/garages/GarageForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  createGarage,
  updateGarage,
  fetchMakes,
  fetchGarageById,
  clearGarageError,
  clearGarageSuccess,
} from '../../store/actions/garageActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import { GARAGE_TYPES, SERVICE_TYPES } from '../../store/actions/actionTypes';

const GarageForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const { currentGarage, vehicleMakes, loading, error, success } = useSelector(
    (state) => state.garages
  );

  const [formData, setFormData] = useState({
    name: '',
    siret: '',
    type: GARAGE_TYPES.INDEPENDENT,
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'FR',
      latitude: 0,
      longitude: 0,
    },
    contact: {
      phone: '',
      email: '',
      website: '',
      emergencyPhone: '',
    },
    services: [],
    specializations: [],
  });

  const [newService, setNewService] = useState({
    type: SERVICE_TYPES.REPAIR,
    description: '',
    priceEstimate: 0,
  });

  const [newSpecialization, setNewSpecialization] = useState({
    makeId: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const styles = {
    container: {
      maxWidth: '1024px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    backButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    section: {
      marginBottom: '2rem',
      paddingBottom: '2rem',
      borderBottom: '1px solid #e5e7eb',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
    },
    required: {
      color: '#dc2626',
      marginLeft: '0.25rem',
    },
    input: {
      padding: '0.625rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      transition: 'border-color 0.2s, box-shadow 0.2s',
    },
    inputFocus: {
      outline: 'none',
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      fontSize: '0.75rem',
      color: '#dc2626',
      marginTop: '0.25rem',
    },
    select: {
      padding: '0.625rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      backgroundColor: '#ffffff',
    },
    textarea: {
      padding: '0.625rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      minHeight: '80px',
      resize: 'vertical',
    },
    servicesSection: {
      marginTop: '1rem',
    },
    serviceItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
      marginBottom: '0.5rem',
    },
    serviceInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap',
    },
    serviceBadge: {
      padding: '0.25rem 0.5rem',
      backgroundColor: '#e0e7ff',
      color: '#3730a3',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    serviceDescription: {
      fontSize: '0.875rem',
      color: '#4b5563',
    },
    servicePrice: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#059669',
    },
    removeButton: {
      padding: '0.25rem',
      backgroundColor: 'transparent',
      border: 'none',
      color: '#9ca3af',
      cursor: 'pointer',
      transition: 'color 0.2s',
      borderRadius: '0.25rem',
    },
    removeButtonHover: {
      color: '#dc2626',
    },
    addServiceForm: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1rem',
      alignItems: 'end',
      marginTop: '1rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    addButton: {
      padding: '0.625rem 1rem',
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    addButtonHover: {
      backgroundColor: '#047857',
    },
    addButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    actionButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    cancelButton: {
      padding: '0.625rem 1.5rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f3f4f6',
    },
    submitButton: {
      padding: '0.625rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    hint: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
  };

  // Fetch garage data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchGarageById(id));
    }
  }, [dispatch, id, isEditMode]);

  // Fetch vehicle makes for specialization
  useEffect(() => {
    dispatch(fetchMakes());
  }, [dispatch]);

  // Populate form with existing data in edit mode
  useEffect(() => {
    if (isEditMode && currentGarage) {
      setFormData({
        name: currentGarage.name || '',
        siret: currentGarage.siret || '',
        type: currentGarage.type || GARAGE_TYPES.INDEPENDENT,
        address: {
          street: currentGarage.address?.street || '',
          city: currentGarage.address?.city || '',
          postalCode: currentGarage.address?.postalCode || '',
          country: currentGarage.address?.country || 'FR',
          latitude: currentGarage.address?.latitude || 0,
          longitude: currentGarage.address?.longitude || 0,
        },
        contact: {
          phone: currentGarage.contact?.phone || '',
          email: currentGarage.contact?.email || '',
          website: currentGarage.contact?.website || '',
          emergencyPhone: currentGarage.contact?.emergencyPhone || '',
        },
        services: currentGarage.services || [],
        specializations: currentGarage.specializations?.map(s => ({
          makeId: s.make.id,
        })) || [],
      });
    }
  }, [isEditMode, currentGarage]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearGarageError());
      dispatch(clearGarageSuccess());
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleAddService = () => {
    if (!newService.type) return;

    setFormData((prev) => ({
      ...prev,
      services: [
        ...prev.services,
        {
          ...newService,
          id: `temp-${Date.now()}`,
        },
      ],
    }));

    setNewService({
      type: SERVICE_TYPES.REPAIR,
      description: '',
      priceEstimate: 0,
    });
  };

  const handleRemoveService = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s.id !== serviceId),
    }));
  };

  const handleAddSpecialization = () => {
    if (!newSpecialization.makeId) return;

    // Check if already added
    if (formData.specializations.some(s => s.makeId === newSpecialization.makeId)) {
      alert('Cette spécialisation est déjà ajoutée.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      specializations: [...prev.specializations, newSpecialization],
    }));

    setNewSpecialization({ makeId: '' });
  };

  const handleRemoveSpecialization = (makeId) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.filter((s) => s.makeId !== makeId),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.siret.trim()) {
      newErrors.siret = 'SIRET is required';
    } else if (!/^\d{14}$/.test(formData.siret)) {
      newErrors.siret = 'SIRET must be 14 digits';
    }
    if (!formData.type) {
      newErrors.type = 'Type is required';
    }

    // Address validation
    if (!formData.address.street.trim()) {
      newErrors['address.street'] = 'Street is required';
    }
    if (!formData.address.city.trim()) {
      newErrors['address.city'] = 'City is required';
    }
    if (!formData.address.postalCode.trim()) {
      newErrors['address.postalCode'] = 'Postal code is required';
    }

    // Email validation if provided
    if (formData.contact.email && !/\S+@\S+\.\S+/.test(formData.contact.email)) {
      newErrors['contact.email'] = 'Invalid email format';
    }

    // Website validation if provided
    if (formData.contact.website && !/^https?:\/\/.+/.test(formData.contact.website)) {
      newErrors['contact.website'] = 'Invalid URL format (must start with http:// or https://)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        address: {
          ...formData.address,
          latitude: formData.address.latitude ? parseFloat(formData.address.latitude) : 0,
          longitude: formData.address.longitude ? parseFloat(formData.address.longitude) : 0,
        },
        services: formData.services.map(({ id, ...service }) => service),
      };

      if (isEditMode) {
        await dispatch(updateGarage(id, submitData));
      } else {
        await dispatch(createGarage(submitData));
      }

      // Navigate back to list after successful submission
      setTimeout(() => {
        navigate('/admin/garages');
      }, 1500);
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/garages');
  };

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? 'Modifier le garage' : 'Ajouter un nouveau garage'}
        </h1>
        <Link
          to="/admin/garages"
          style={styles.backButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
          }}
        >
          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour à la liste
        </Link>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearGarageError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearGarageSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Informations de base</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Nom du garage
                <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.name ? styles.inputError : {}),
                }}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Entrez le nom du garage"
              />
              {errors.name && <div style={styles.errorText}>{errors.name}</div>}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                SIRET
                <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="siret"
                value={formData.siret}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.siret ? styles.inputError : {}),
                }}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Entrez le numéro SIRET (14 chiffres)"
                maxLength="14"
              />
              {errors.siret && <div style={styles.errorText}>{errors.siret}</div>}
              <div style={styles.hint}>Doit contenir 14 chiffres</div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Type de garage
                <span style={styles.required}>*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                style={styles.select}
              >
                {Object.entries(GARAGE_TYPES).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value.charAt(0) + value.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Adresse</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Rue
                <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors['address.street'] ? styles.inputError : {}),
                }}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Entrez la rue"
              />
              {errors['address.street'] && (
                <div style={styles.errorText}>{errors['address.street']}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Ville
                <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors['address.city'] ? styles.inputError : {}),
                }}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Entrez la ville"
              />
              {errors['address.city'] && (
                <div style={styles.errorText}>{errors['address.city']}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Code Postal
                <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="address.postalCode"
                value={formData.address.postalCode}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors['address.postalCode'] ? styles.inputError : {}),
                }}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Entrez le code postal"
              />
              {errors['address.postalCode'] && (
                <div style={styles.errorText}>{errors['address.postalCode']}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Country</label>
              <input
                type="text"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Entrez le pays"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Latitude</label>
              <input
                type="number"
                step="any"
                name="address.latitude"
                value={formData.address.latitude}
                onChange={handleChange}
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Latitude"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Longitude</label>
              <input
                type="number"
                step="any"
                name="address.longitude"
                value={formData.address.longitude}
                onChange={handleChange}
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Longitude"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Informations de contact</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Téléphone</label>
              <input
                type="tel"
                name="contact.phone"
                value={formData.contact.phone}
                onChange={handleChange}
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Entrez le numéro de téléphone"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                name="contact.email"
                value={formData.contact.email}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors['contact.email'] ? styles.inputError : {}),
                }}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Entrez l'adresse email"
              />
              {errors['contact.email'] && (
                <div style={styles.errorText}>{errors['contact.email']}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Website</label>
              <input
                type="url"
                name="contact.website"
                value={formData.contact.website}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors['contact.website'] ? styles.inputError : {}),
                }}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="https://..."
              />
              {errors['contact.website'] && (
                <div style={styles.errorText}>{errors['contact.website']}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Téléphone d'urgence</label>
              <input
                type="tel"
                name="contact.emergencyPhone"
                value={formData.contact.emergencyPhone}
                onChange={handleChange}
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.outline = styles.inputFocus.outline;
                  e.target.style.borderColor = styles.inputFocus.borderColor;
                  e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                  e.target.style.outline = '';
                  e.target.style.borderColor = '';
                  e.target.style.boxShadow = '';
                }}
                placeholder="Entrez le numéro d'urgence 24/7"
              />
            </div>
          </div>
        </div>

        {/* Services Offered */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Services Offerts</h2>
          
          {/* Existing Services */}
          {formData.services.map((service) => (
            <div key={service.id} style={styles.serviceItem}>
              <div style={styles.serviceInfo}>
                <span style={styles.serviceBadge}>{service.type}</span>
                {service.description && (
                  <span style={styles.serviceDescription}>{service.description}</span>
                )}
                {service.priceEstimate && (
                  <span style={styles.servicePrice}>€{service.priceEstimate}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRemoveService(service.id)}
                style={styles.removeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = styles.removeButtonHover.color;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = styles.removeButton.color;
                }}
                title="Supprimer le service"
              >
                <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}

          {/* Add New Service Form */}
          <div style={styles.addServiceForm}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Type de service</label>
              <select
                value={newService.type}
                onChange={(e) => setNewService({ ...newService, type: e.target.value })}
                style={styles.select}
              >
                {Object.entries(SERVICE_TYPES).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value.charAt(0) + value.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Description</label>
              <input
                type="text"
                value={newService.description}
                onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                style={styles.input}
                placeholder="Entrez la description du service"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Estimation du prix (€)</label>
              <input
                type="number"
                step="0.01"
                value={newService.priceEstimate}
                onChange={(e) => setNewService({ ...newService, priceEstimate: parseInt(e.target.value) || 0 })}
                style={styles.input}
                placeholder="0.00"
              />
            </div>

            <button
              type="button"
              onClick={handleAddService}
              disabled={!newService.type}
              style={{
                ...styles.addButton,
                ...(!newService.type ? styles.addButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (newService.type) {
                  e.currentTarget.style.backgroundColor = styles.addButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (newService.type) {
                  e.currentTarget.style.backgroundColor = styles.addButton.backgroundColor;
                }
              }}
            >
              Ajouter un service
            </button>
          </div>
        </div>

        {/* Specializations */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Spécialisations</h2>
          
          {/* Existing Specializations */}
          {formData.specializations.map((spec) => {
            const make = vehicleMakes.find(m => m.id === spec.makeId);
            return (
              <div key={spec.makeId} style={styles.serviceItem}>
                <div style={styles.serviceInfo}>
                  <span style={styles.serviceBadge}>{make?.name || spec.makeId}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveSpecialization(spec.makeId)}
                  style={styles.removeButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = styles.removeButtonHover.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = styles.removeButton.color;
                  }}
                  title="Supprimer la spécialisation"
                >
                  <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}

          {/* Add New Specialization */}
          <div style={styles.addServiceForm}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Marque de véhicule</label>
              <select
                value={newSpecialization.makeId}
                onChange={(e) => setNewSpecialization({ makeId: e.target.value })}
                style={styles.select}
              >
                <option value="">électionnez une marque</option>
                {vehicleMakes.map((make) => (
                  <option key={make.id} value={make.id}>
                    {make.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={handleAddSpecialization}
              disabled={!newSpecialization.makeId}
              style={{
                ...styles.addButton,
                ...(!newSpecialization.makeId ? styles.addButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (newSpecialization.makeId) {
                  e.currentTarget.style.backgroundColor = styles.addButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (newSpecialization.makeId) {
                  e.currentTarget.style.backgroundColor = styles.addButton.backgroundColor;
                }
              }}
            >
              Ajouter une spécialisation
            </button>
          </div>
        </div>

        {/* Form Actions */}
        <div style={styles.actionButtons}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting
              ? (isEditMode ? 'Mise à jour en cours...' : 'Création en cours...')
              : (isEditMode ? 'Mettre à jour le garage' : 'Créer un garage')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default GarageForm;